/**
 * 客户端更新管理器
 * 版本比较 + 下载 + 热更新
 */
export interface UpdateInfo {
  currentVersion: string
  latestVersion: string
  downloadUrl?: string
  releaseNotes?: string
  mandatory?: boolean
}

export interface UpdateManagerOptions {
  currentVersion: string
  checkInterval?: number
  autoDownload?: boolean
}

export type CheckFn = () => Promise<UpdateInfo | null>

export class UpdateManager {
  private options: Required<UpdateManagerOptions>
  private timer: ReturnType<typeof setInterval> | null = null
  private lastCheck: UpdateInfo | null = null
  private updateHistory: Array<{ version: string; timestamp: number; success: boolean }> = []

  constructor(options: UpdateManagerOptions) {
    this.options = {
      ...options,
      checkInterval: options.checkInterval ?? 3600000,
      autoDownload: options.autoDownload ?? false,
    }
  }

  /** 检查更新 */
  async check(checkFn: CheckFn): Promise<UpdateInfo | null> {
    const info = await checkFn()
    this.lastCheck = info
    return info
  }

  /** 版本比较 */
  compareVersions(a: string, b: string): number {
    const pa = a.split('.').map(Number)
    const pb = b.split('.').map(Number)
    for (let i = 0; i < Math.max(pa.length, pb.length); i++) {
      const na = pa[i] ?? 0
      const nb = pb[i] ?? 0
      if (na > nb) return 1
      if (na < nb) return -1
    }
    return 0
  }

  /** 是否有可用更新 */
  hasUpdate(): boolean {
    if (!this.lastCheck) return false
    return this.compareVersions(this.lastCheck.latestVersion, this.options.currentVersion) > 0
  }

  /** 记录更新 */
  recordUpdate(version: string, success: boolean): void {
    this.updateHistory.push({ version, timestamp: Date.now(), success })
  }

  /** 开始自动检查 */
  startAutoCheck(checkFn: CheckFn): void {
    this.timer = setInterval(() => this.check(checkFn), this.options.checkInterval)
  }

  /** 停止自动检查 */
  stop(): void {
    if (this.timer) { clearInterval(this.timer); this.timer = null }
  }

  /** 获取当前版本 */
  get currentVersion(): string {
    return this.options.currentVersion
  }

  /** 获取上次检查结果 */
  getLastCheck(): UpdateInfo | null {
    return this.lastCheck
  }

  /** 获取更新历史 */
  getHistory(): typeof this.updateHistory {
    return [...this.updateHistory]
  }
}
